import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hand, Vibrate, Zap } from "lucide-react";

interface GestureConfig {
  enabled: boolean;
  hapticFeedback: boolean;
  swipeThreshold: number;
  longPressDelay: number;
  doubleTapAction: "flag" | "bookmark" | "none";
}

const DEFAULT_CONFIG: GestureConfig = {
  enabled: true,
  hapticFeedback: true,
  swipeThreshold: 50,
  longPressDelay: 500,
  doubleTapAction: "bookmark"
};

export const GestureSettings = () => {
  const [config, setConfig] = useState<GestureConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    const saved = localStorage.getItem("gestureConfig");
    if (saved) {
      try {
        setConfig(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse gesture config", e);
      }
    }
  }, []);

  const updateConfig = (updates: Partial<GestureConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    localStorage.setItem("gestureConfig", JSON.stringify(newConfig));
  };

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Hand className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold">Gesture Settings</h2>
      </div>

      {/* Enable Gestures */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Enable Gesture Navigation</Label>
          <p className="text-sm text-muted-foreground">
            Swipe, long press, and double tap controls
          </p>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(enabled) => updateConfig({ enabled })}
        />
      </div>

      {/* Haptic Feedback */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5 flex items-center gap-2">
          <Vibrate className="h-4 w-4 text-muted-foreground" />
          <div>
            <Label>Haptic Feedback</Label>
            <p className="text-sm text-muted-foreground">
              Vibration on gestures
            </p>
          </div>
        </div>
        <Switch
          checked={config.hapticFeedback}
          onCheckedChange={(hapticFeedback) => updateConfig({ hapticFeedback })}
          disabled={!config.enabled}
        />
      </div>

      {/* Swipe Sensitivity */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-muted-foreground" />
          <Label>Swipe Sensitivity</Label>
        </div>
        <div className="space-y-2">
          <Slider
            value={[config.swipeThreshold]}
            onValueChange={([swipeThreshold]) => updateConfig({ swipeThreshold })}
            min={30}
            max={100}
            step={10}
            disabled={!config.enabled}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>More Sensitive</span>
            <span>{config.swipeThreshold}px</span>
            <span>Less Sensitive</span>
          </div>
        </div>
      </div>

      {/* Long Press Delay */}
      <div className="space-y-3">
        <Label>Long Press Delay</Label>
        <div className="space-y-2">
          <Slider
            value={[config.longPressDelay]}
            onValueChange={([longPressDelay]) => updateConfig({ longPressDelay })}
            min={300}
            max={1000}
            step={100}
            disabled={!config.enabled}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Faster</span>
            <span>{config.longPressDelay}ms</span>
            <span>Slower</span>
          </div>
        </div>
      </div>

      {/* Double Tap Action */}
      <div className="space-y-2">
        <Label>Double Tap Action</Label>
        <Select
          value={config.doubleTapAction}
          onValueChange={(doubleTapAction: typeof config.doubleTapAction) => 
            updateConfig({ doubleTapAction })
          }
          disabled={!config.enabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bookmark">Bookmark Question</SelectItem>
            <SelectItem value="flag">Flag for Review</SelectItem>
            <SelectItem value="none">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Gesture Guide */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
        <p className="font-medium">Gesture Guide:</p>
        <ul className="space-y-1 text-muted-foreground">
          <li>• Swipe Left/Right: Next/Previous question</li>
          <li>• Swipe Up/Down: Show/Hide explanation</li>
          <li>• Long Press: Bookmark question</li>
          <li>• Double Tap: {
            config.doubleTapAction === "bookmark" ? "Bookmark" :
            config.doubleTapAction === "flag" ? "Flag for review" :
            "Disabled"
          }</li>
        </ul>
      </div>
    </Card>
  );
};

export const getGestureConfig = (): GestureConfig => {
  const saved = localStorage.getItem("gestureConfig");
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return DEFAULT_CONFIG;
    }
  }
  return DEFAULT_CONFIG;
};
